import { useCallback, useEffect, useRef, useState } from "react";
import criticalOperationStore from "stores/criticalOperationStore";

/**
 * Drop-in replacement for `useState(false)` on a component's "submitting"
 * flag. Mirrors every true/false transition into the shared
 * criticalOperationStore so the recovery-reload watcher can tell a
 * payment/booking submission is in flight and defer its reload.
 */
const useCriticalSubmitting = (initial = false) => {
  const [submitting, setSubmittingState] = useState(initial);
  // The unmount cleanup below runs in an effect with `[]` deps, so it would
  // otherwise only ever see the `submitting` value from the initial render
  // (a stale closure) — this ref is updated synchronously on every
  // transition so the cleanup always reads the current value.
  const submittingRef = useRef(initial);

  const setSubmitting = useCallback((value) => {
    setSubmittingState((prev) => {
      const next = typeof value === "function" ? value(prev) : value;
      if (next && !prev) criticalOperationStore.begin();
      if (!next && prev) criticalOperationStore.end();
      submittingRef.current = next;
      return next;
    });
  }, []);

  // Safety net: if the component unmounts mid-submit (nav away, error boundary),
  // release the critical-operation slot instead of leaving it stuck forever.
  useEffect(() => {
    return () => {
      if (submittingRef.current) criticalOperationStore.end();
    };
  }, []);

  return [submitting, setSubmitting];
};

export default useCriticalSubmitting;
